import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MoreVert } from '@mui/icons-material';
import { IconButton, Menu, MenuItem, TableCell, TableRow, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { ApiService, RetrieveDocumentGenerationJob } from 'api';
import { useStore } from 'common/store';
import { useResponse } from 'services/hooks';
import { formatDate } from 'utilities';
import { titleCaseEnumValue } from 'utilities/titleCaseEnumValue';
import DocumentGenerationJobTableItemProps from './DocumentGenerationJobTableItem.props';
import {
  AnimatedStatusChip,
  GradientBorderWrapper,
  PlainStatusChip,
  StatusChip,
} from './DocumentGenerationJobTableItem.styles';
import { QUERY_CONSTANTS, RESULTS_DISPLAY, TABLE_ROW_CONSTANTS } from '../../../constants';
import { AIGradientSpinner } from '../../AIGradient';
import ResultsButton from '../../ResultsButton/ResultsButton';
import ResultsDisplayModal from '../../ResultsDisplayModal/ResultsDisplayModal';
import DocumentGenerationJobTableItemCapTableMenu from '../DocumentGenerationJobTableItemMenu/DocumentGenerationJobTableItemCapTableMenu/DocumentGenerationJobTableItemCapTableMenu';
import DocumentGenerationJobTableItemFinMapMenu from '../DocumentGenerationJobTableItemMenu/DocumentGenerationJobTableItemFinMapMenu/DocumentGenerationJobTableItemFinMapMenu';

const DocumentGenerationJobsTableItem = ({ generationJob, onStatusChange }: DocumentGenerationJobTableItemProps) => {
  const [resultsDisplayModalOpen, setResultsDisplayModalOpen] = useState<boolean>(false);
  const [currentGenerationJob, setCurrentGenerationJob] = useState<RetrieveDocumentGenerationJob>(generationJob);
  const DEFAULT_REFETCH_INTERVAL_IN_MS = 10_000;
  const [{ companyInfo }] = useStore() as any;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { successNotification, errorNotification } = useResponse();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (error) {
      errorNotification(TABLE_ROW_CONSTANTS.ERROR_COPYING);
    }
  };

  const { apiCompanyDocumentGenerationJobsRead } = ApiService;

  const { data } = useQuery({
    queryKey: [QUERY_CONSTANTS.getDocumentGenerationJobById, companyInfo.id, currentGenerationJob.job_id],
    queryFn: async (): Promise<RetrieveDocumentGenerationJob> => {
      const response = await apiCompanyDocumentGenerationJobsRead(companyInfo.id, currentGenerationJob.job_id);
      return response as any;
    },
    refetchInterval:
      currentGenerationJob.status === RetrieveDocumentGenerationJob.status.COMPLETED
        ? false
        : DEFAULT_REFETCH_INTERVAL_IN_MS,
  });

  const toggleResultsModal = useCallback(() => {
    setResultsDisplayModalOpen(prev => !prev);
  }, []);

  useEffect(() => {
    if (!data) return;

    setCurrentGenerationJob(data);

    if (data.status) {
      onStatusChange && onStatusChange(data.status, data);
    }
  }, [data, onStatusChange]);


  const GenerationJobMenuOptions = useMemo(() => {
    switch (currentGenerationJob.processing_type) {
      case RetrieveDocumentGenerationJob.processing_type.FINANCIAL_MAPPING:
        return <DocumentGenerationJobTableItemFinMapMenu generationJob={currentGenerationJob} onClose={handleClose} />;
      case RetrieveDocumentGenerationJob.processing_type.CAP_TABLE:
        return (
          <DocumentGenerationJobTableItemCapTableMenu generationJob={currentGenerationJob} onClose={handleClose} />
        );
      default:
        return null;
        break;
    }
  }, [currentGenerationJob, handleClose])

  const { status } = currentGenerationJob;
  let statusCell: React.ReactNode;
  if (status === RetrieveDocumentGenerationJob.status.COMPLETED) {
    statusCell = (
      <GradientBorderWrapper>
        <StatusChip label={titleCaseEnumValue(status!)} />
      </GradientBorderWrapper>
    );
  } else if (
    status === RetrieveDocumentGenerationJob.status.IN_PROGRESS ||
    status === RetrieveDocumentGenerationJob.status.PENDING
  ) {
    statusCell = (
      <GradientBorderWrapper $animated>
        <AnimatedStatusChip avatar={<AIGradientSpinner />} label={titleCaseEnumValue(status!)} />
      </GradientBorderWrapper>
    );
  } else {
    statusCell = <PlainStatusChip label={titleCaseEnumValue(status!)} />;
  }

  return (
    <>
      {currentGenerationJob.results && currentGenerationJob.results?.length > 8 && (
        <ResultsDisplayModal
          onClose={toggleResultsModal}
          open={resultsDisplayModalOpen}
          resultsJsonString={currentGenerationJob.results}
        />
      )}
      <TableRow>
        <TableCell>{statusCell}</TableCell>
        <TableCell>
          <Typography>{titleCaseEnumValue(currentGenerationJob.processing_type!)}</Typography>
        </TableCell>
        <TableCell>
          <Typography>
            {currentGenerationJob.created_by?.first_name} {currentGenerationJob.created_by?.last_name}
          </Typography>
        </TableCell>
        <TableCell>
          {currentGenerationJob.created_at && (
            <Typography>{formatDate({ date: new Date(currentGenerationJob.created_at) })}</Typography>
          )}
        </TableCell>
        <TableCell>
          <Typography style={{ whiteSpace: 'pre-line' }}>
            {currentGenerationJob.reference_files.map(i => `${i.filename}.${i.file_type}`).join('\n')}
          </Typography>
        </TableCell>
        <TableCell width={'30%'} style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
          {currentGenerationJob.results && currentGenerationJob.results?.length > 8 ? (
            <ResultsButton onClick={toggleResultsModal} jsonResultsString={currentGenerationJob.results} />
          ) : (
            <Typography variant="caption" style={{ fontSize: '1rem' }}>
              {currentGenerationJob.status === RetrieveDocumentGenerationJob.status.FAILED
                ? RESULTS_DISPLAY.FAILURE_CONTENT
                : RESULTS_DISPLAY.PENDING_RESULTS_CONTENT(currentGenerationJob.reference_files.length)}
            </Typography>
          )}
        </TableCell>
        <TableCell>
          <IconButton onClick={handleClick}>
            <MoreVert />
          </IconButton>
          <Menu anchorEl={anchorEl} keepMounted open={Boolean(anchorEl)} onClose={handleClose}>
            <MenuItem
              onClick={() => {
                copyToClipboard(currentGenerationJob.job_id);
                successNotification(TABLE_ROW_CONSTANTS.COPY_SUCCESS);
                handleClose();
              }}>
              {TABLE_ROW_CONSTANTS.COPY_ID}
            </MenuItem>
            {GenerationJobMenuOptions}
          </Menu>
        </TableCell>
      </TableRow>
    </>
  );
};

export default DocumentGenerationJobsTableItem;
